import * as THREE from 'three';

export const LEGACY_TO_BROWSER_WORLD_SCALE = 0.1;
export const LEGACY_CAMERA_NEAR = 0.1 * LEGACY_TO_BROWSER_WORLD_SCALE;
export const LEGACY_CAMERA_FAR = 300 * LEGACY_TO_BROWSER_WORLD_SCALE;
export const LEGACY_CAMERA_ORBIT_SPEED = 2;
export const LEGACY_CAMERA_ZOOM_SPEED = 1;
export const FIRST_PERSON_EYE_HEIGHT = 1.55;

const INITIAL_AZIMUTH = -1;
const MINIMUM_ZOOM = 0;
const MAXIMUM_ZOOM = 1;
const PHONE_MAXIMUM_ZOOM = 2;
const FIRST_PERSON_PITCH_SPEED = 1.15;
const FIRST_PERSON_MINIMUM_PITCH = -0.9;
const FIRST_PERSON_MAXIMUM_PITCH = 0.75;
const FIRST_PERSON_LOOK_DISTANCE = 10;
const TARGET_DISTANCE_BASE = -16;
const FIXED_CAMERA_HORIZONTAL_BASE = 22;
const FIXED_CAMERA_HEIGHT_BASE = 30;
const FIXED_TARGET_OFFSET_BASE = -11;
const ZOOM_DISTANCE = 8.5;
const MOTION_HISTORY_FRAMES = 60;
const FRAME_RATE_HISTORY_FRAMES = 30;
const REFERENCE_FRAME_SECONDS = 1 / 60;
const MOTION_RESPONSE = 0.14;
const MOTION_MEMORY = 0.16;
const MOTION_COUNTER_SWAY = 0.09;
const MOTION_STARTUP_DECAY_PER_SECOND = 0.5;
const MOTION_FRAME_RATE_COMPENSATION = 25;
const MOTION_SNAP_DISTANCE = 60 * LEGACY_TO_BROWSER_WORLD_SCALE;
export type LegacyCameraPose = Readonly<{
  position: THREE.Vector3;
  target: THREE.Vector3;
  verticalFovDegrees: number;
}>;

export type LegacyCameraPerspective = 'third-person' | 'first-person';

/**
 * Original nE chase-camera geometry, converted from ten-unit JME terrain spacing
 * to the browser coordinate system. Arrow input is deliberately separate from
 * WASD player/focus movement, matching the running Java client.
 */
export class LegacyCameraRig {
  private azimuth = INITIAL_AZIMUTH;
  private zoom = MAXIMUM_ZOOM;
  private maximumZoom = MAXIMUM_ZOOM;
  private extendedZoomMotion = false;
  private pitch = 0;
  private perspective: LegacyCameraPerspective = 'third-person';
  private threeDimensionalEnabled = true;
  private firstPersonEyeHeight = FIRST_PERSON_EYE_HEIGHT;
  private readonly positionMotion = new LegacyCameraMotionFilter();
  private readonly targetMotion = new LegacyCameraMotionFilter();
  private readonly frameRates = new Float32Array(FRAME_RATE_HISTORY_FRAMES);
  private readonly desiredPosition = new THREE.Vector3();
  private readonly desiredTarget = new THREE.Vector3();
  private readonly motionError = new THREE.Vector3();
  private readonly motionCorrection = new THREE.Vector3();
  private readonly smoothedTarget = new THREE.Vector3();
  private frameRateIndex = 0;
  private startupStabilization = 1;
  private motionInitialized = false;
  private renderedAzimuth = INITIAL_AZIMUTH;

  update(deltaSeconds: number, orbitInput: number, zoomInput: number): void {
    this.azimuth += orbitInput * LEGACY_CAMERA_ORBIT_SPEED * deltaSeconds;
    if (this.perspective === 'first-person') {
      this.pitch = THREE.MathUtils.clamp(
        this.pitch - zoomInput * FIRST_PERSON_PITCH_SPEED * deltaSeconds,
        FIRST_PERSON_MINIMUM_PITCH,
        FIRST_PERSON_MAXIMUM_PITCH,
      );
      return;
    }
    this.zoom = THREE.MathUtils.clamp(
      this.zoom + zoomInput * LEGACY_CAMERA_ZOOM_SPEED * deltaSeconds,
      MINIMUM_ZOOM,
      this.maximumZoom,
    );
  }

  applyInputImpulse(orbitRadians: number, zoomDelta: number): void {
    this.azimuth += orbitRadians;
    if (this.perspective === 'first-person') {
      this.pitch = THREE.MathUtils.clamp(
        this.pitch - zoomDelta,
        FIRST_PERSON_MINIMUM_PITCH,
        FIRST_PERSON_MAXIMUM_PITCH,
      );
      return;
    }
    this.zoom = THREE.MathUtils.clamp(this.zoom + zoomDelta, MINIMUM_ZOOM, this.maximumZoom);
  }

  /** Default genuine phone entry to its existing maximum; preserve manual zoom within the mode. */
  setPhoneGesturesEnabled(enabled: boolean): void {
    const enteringPhone = this.maximumZoom !== PHONE_MAXIMUM_ZOOM && enabled;
    const leavingPhone = this.maximumZoom === PHONE_MAXIMUM_ZOOM && !enabled;
    this.maximumZoom = enabled ? PHONE_MAXIMUM_ZOOM : MAXIMUM_ZOOM;
    if (enteringPhone) {
      this.zoom = PHONE_MAXIMUM_ZOOM;
      this.resetMotion();
    } else if (this.zoom > this.maximumZoom || (leavingPhone && this.extendedZoomMotion)) {
      this.zoom = Math.min(this.zoom, this.maximumZoom);
      // Do not let chase-camera history linger outside the restored desktop range.
      this.resetMotion();
    }
  }

  get currentAzimuth(): number {
    return this.motionInitialized ? this.renderedAzimuth : this.azimuth;
  }

  get currentPerspective(): LegacyCameraPerspective {
    return this.perspective;
  }

  get isThreeDimensionalEnabled(): boolean {
    return this.threeDimensionalEnabled;
  }

  setPerspective(perspective: LegacyCameraPerspective): void {
    if (this.perspective === perspective) return;
    this.perspective = perspective;
    this.resetMotion();
  }

  setThreeDimensionalEnabled(enabled: boolean): void {
    this.threeDimensionalEnabled = enabled;
  }

  toggleThreeDimensional(): boolean {
    this.threeDimensionalEnabled = !this.threeDimensionalEnabled;
    return this.threeDimensionalEnabled;
  }

  setFirstPersonEyeHeight(height: number): void {
    if (Number.isFinite(height) && height > 0) this.firstPersonEyeHeight = height;
  }

  resetMotion(): void {
    this.extendedZoomMotion = false;
    this.positionMotion.reset();
    this.targetMotion.reset();
    this.startupStabilization = 1;
    this.motionInitialized = false;
    this.renderedAzimuth = this.azimuth;
  }

  pose(focusX: number, groundHeight: number, focusZ: number): LegacyCameraPose {
    if (this.perspective === 'first-person') {
      return this.firstPersonPose(focusX, groundHeight, focusZ);
    }
    if (!this.threeDimensionalEnabled) {
      return this.fixedThirdPersonPose(focusX, groundHeight, focusZ);
    }
    const horizontalDistance = this.horizontalDistance();
    const cameraHeight = this.cameraHeight();
    const targetDistance = this.targetDistance();
    return {
      position: new THREE.Vector3(
        focusX + Math.cos(this.azimuth) * horizontalDistance,
        groundHeight + cameraHeight,
        focusZ + Math.sin(this.azimuth) * horizontalDistance,
      ),
      target: new THREE.Vector3(
        focusX + Math.cos(this.azimuth) * targetDistance,
        groundHeight,
        focusZ + Math.sin(this.azimuth) * targetDistance,
      ),
      verticalFovDegrees: this.verticalFovDegrees(),
    };
  }

  /** Applies the rig without allocating vectors in the render loop. */
  apply(
    camera: THREE.PerspectiveCamera,
    focusX: number,
    groundHeight: number,
    focusZ: number,
    deltaSeconds = REFERENCE_FRAME_SECONDS,
  ): void {
    if (this.perspective === 'first-person') {
      const eyeHeight = groundHeight + this.firstPersonEyeHeight;
      const horizontalLookDistance = Math.cos(this.pitch) * FIRST_PERSON_LOOK_DISTANCE;
      camera.position.set(focusX, eyeHeight, focusZ);
      camera.lookAt(
        focusX - Math.cos(this.azimuth) * horizontalLookDistance,
        eyeHeight + Math.sin(this.pitch) * FIRST_PERSON_LOOK_DISTANCE,
        focusZ - Math.sin(this.azimuth) * horizontalLookDistance,
      );
      this.applyVerticalFov(camera, effectiveLegacyVerticalFov(45));
      this.motionInitialized = false;
      this.renderedAzimuth = this.azimuth;
      return;
    }
    if (!this.threeDimensionalEnabled) {
      const cameraOffset = this.fixedCameraOffset();
      const targetOffset = this.fixedTargetOffset();
      this.desiredPosition.set(
        focusX + cameraOffset,
        groundHeight + this.fixedCameraHeight(),
        focusZ + cameraOffset,
      );
      this.desiredTarget.set(focusX + targetOffset, groundHeight, focusZ + targetOffset);
      this.applyNaturalMotion(camera, deltaSeconds);
      this.applyVerticalFov(camera, this.verticalFovDegrees());
      return;
    }
    const horizontalDistance = this.horizontalDistance();
    const targetDistance = this.targetDistance();
    this.desiredPosition.set(
      focusX + Math.cos(this.azimuth) * horizontalDistance,
      groundHeight + this.cameraHeight(),
      focusZ + Math.sin(this.azimuth) * horizontalDistance,
    );
    this.desiredTarget.set(
      focusX + Math.cos(this.azimuth) * targetDistance,
      groundHeight,
      focusZ + Math.sin(this.azimuth) * targetDistance,
    );
    this.applyNaturalMotion(camera, deltaSeconds);
    this.applyVerticalFov(camera, this.verticalFovDegrees());
  }

  private applyNaturalMotion(camera: THREE.PerspectiveCamera, deltaSeconds: number): void {
    // Even after logical zoom returns to desktop bounds, filtered motion can retain extra zoom.
    if (this.zoom > MAXIMUM_ZOOM) this.extendedZoomMotion = true;
    const frameSeconds =
      Number.isFinite(deltaSeconds) && deltaSeconds > 0 ? deltaSeconds : REFERENCE_FRAME_SECONDS;
    this.updateMotionCoefficients(frameSeconds);

    const snapForDistance =
      this.motionInitialized &&
      camera.position.distanceToSquared(this.desiredPosition) >= MOTION_SNAP_DISTANCE ** 2;
    if (!this.motionInitialized || snapForDistance) {
      camera.position.copy(this.desiredPosition);
      this.smoothedTarget.copy(this.desiredTarget);
      this.positionMotion.reset();
      this.targetMotion.reset();
      if (snapForDistance) this.startupStabilization = 1;
      this.motionInitialized = true;
    } else {
      this.motionError.copy(this.desiredPosition).sub(camera.position);
      camera.position.add(this.positionMotion.step(this.motionError, this.motionCorrection));
      this.motionError.copy(this.desiredTarget).sub(this.smoothedTarget);
      this.smoothedTarget.add(this.targetMotion.step(this.motionError, this.motionCorrection));
    }
    camera.lookAt(this.smoothedTarget);
    this.renderedAzimuth = Math.atan2(
      camera.position.z - this.smoothedTarget.z,
      camera.position.x - this.smoothedTarget.x,
    );
  }

  /**
   * Source nE derives rL's coefficients from a rolling frame-rate sample. At a steady 60 FPS the
   * filter uses 0.14 response, 0.16 history, and 0.09 counter-motion; lower or irregular frame
   * rates blend toward a direct correction so the camera remains stable.
   */
  private updateMotionCoefficients(deltaSeconds: number): void {
    this.frameRates[this.frameRateIndex] = 1 / deltaSeconds;
    this.frameRateIndex = (this.frameRateIndex + 1) % this.frameRates.length;
    let averageFramesPerSecond = 0;
    for (const frameRate of this.frameRates) averageFramesPerSecond += frameRate;
    averageFramesPerSecond /= this.frameRates.length;

    this.startupStabilization = Math.max(
      0,
      this.startupStabilization - deltaSeconds * MOTION_STARTUP_DECAY_PER_SECOND,
    );
    const stabilityBlend = THREE.MathUtils.clamp(
      (1 / averageFramesPerSecond - REFERENCE_FRAME_SECONDS) * MOTION_FRAME_RATE_COMPENSATION +
        this.startupStabilization,
      0,
      1,
    );
    const frameScale = deltaSeconds / REFERENCE_FRAME_SECONDS;
    const response = THREE.MathUtils.lerp(MOTION_RESPONSE * frameScale, 1, stabilityBlend);
    const memory = THREE.MathUtils.lerp(MOTION_MEMORY * frameScale, 0, stabilityBlend);
    const counterSway = THREE.MathUtils.lerp(MOTION_COUNTER_SWAY * frameScale, 0, stabilityBlend);
    this.positionMotion.setCoefficients(response, memory, counterSway);
    this.targetMotion.setCoefficients(response, memory, counterSway);
  }

  private firstPersonPose(focusX: number, groundHeight: number, focusZ: number): LegacyCameraPose {
    const eyeHeight = groundHeight + this.firstPersonEyeHeight;
    const horizontalLookDistance = Math.cos(this.pitch) * FIRST_PERSON_LOOK_DISTANCE;
    return {
      position: new THREE.Vector3(focusX, eyeHeight, focusZ),
      target: new THREE.Vector3(
        focusX - Math.cos(this.azimuth) * horizontalLookDistance,
        eyeHeight + Math.sin(this.pitch) * FIRST_PERSON_LOOK_DISTANCE,
        focusZ - Math.sin(this.azimuth) * horizontalLookDistance,
      ),
      verticalFovDegrees: effectiveLegacyVerticalFov(45),
    };
  }

  private fixedThirdPersonPose(
    focusX: number,
    groundHeight: number,
    focusZ: number,
  ): LegacyCameraPose {
    const cameraOffset = this.fixedCameraOffset();
    const targetOffset = this.fixedTargetOffset();
    return {
      position: new THREE.Vector3(
        focusX + cameraOffset,
        groundHeight + this.fixedCameraHeight(),
        focusZ + cameraOffset,
      ),
      target: new THREE.Vector3(focusX + targetOffset, groundHeight, focusZ + targetOffset),
      verticalFovDegrees: this.verticalFovDegrees(),
    };
  }

  private applyVerticalFov(camera: THREE.PerspectiveCamera, verticalFovDegrees: number): void {
    if (camera.fov === verticalFovDegrees) return;
    camera.fov = verticalFovDegrees;
    camera.updateProjectionMatrix();
  }

  private horizontalDistance(): number {
    return (27 + ZOOM_DISTANCE * this.zoom) * LEGACY_TO_BROWSER_WORLD_SCALE;
  }

  private cameraHeight(): number {
    return (23 + ZOOM_DISTANCE * this.zoom) * LEGACY_TO_BROWSER_WORLD_SCALE;
  }

  private targetDistance(): number {
    return (TARGET_DISTANCE_BASE + ZOOM_DISTANCE * this.zoom) * LEGACY_TO_BROWSER_WORLD_SCALE;
  }

  private fixedCameraOffset(): number {
    return (
      (FIXED_CAMERA_HORIZONTAL_BASE + ZOOM_DISTANCE * this.zoom) * LEGACY_TO_BROWSER_WORLD_SCALE
    );
  }

  private fixedCameraHeight(): number {
    return (FIXED_CAMERA_HEIGHT_BASE + ZOOM_DISTANCE * this.zoom) * LEGACY_TO_BROWSER_WORLD_SCALE;
  }

  private fixedTargetOffset(): number {
    return (FIXED_TARGET_OFFSET_BASE + ZOOM_DISTANCE * this.zoom) * LEGACY_TO_BROWSER_WORLD_SCALE;
  }

  private verticalFovDegrees(): number {
    return effectiveLegacyVerticalFov(38 + 7 * this.zoom);
  }
}

/** Exact browser-space port of the source client's com.nordclientpc.rL motion filter. */
class LegacyCameraMotionFilter {
  private readonly history = Array.from(
    { length: MOTION_HISTORY_FRAMES },
    () => new THREE.Vector3(),
  );
  private readonly historySum = new THREE.Vector3();
  private readonly counterMotion = new THREE.Vector3();
  private response = MOTION_RESPONSE;
  private memory = MOTION_MEMORY / MOTION_HISTORY_FRAMES;
  private counterSway = MOTION_COUNTER_SWAY;
  private historyIndex = 0;

  setCoefficients(response: number, memory: number, counterSway: number): void {
    this.response = response;
    this.memory = memory;
    this.counterSway = counterSway;
  }

  step(input: THREE.Vector3, output: THREE.Vector3): THREE.Vector3 {
    output.copy(input).multiplyScalar(this.response);
    this.historySum.set(0, 0, 0);
    for (const sample of this.history) this.historySum.add(sample);
    output.addScaledVector(this.historySum, this.memory / MOTION_HISTORY_FRAMES);
    const oldestSample = this.history[this.historyIndex];
    if (oldestSample === undefined) return output;
    this.counterMotion.copy(oldestSample).sub(input).multiplyScalar(this.counterSway);
    output.add(this.counterMotion);

    this.historyIndex = (this.historyIndex + 1) % this.history.length;
    const nextSample = this.history[this.historyIndex];
    if (nextSample !== undefined) nextSample.copy(output);
    return output;
  }

  reset(): void {
    for (const sample of this.history) sample.set(0, 0, 0);
  }
}

/**
 * JME's bundled AbstractCamera computes top = near * tan(declaredFov) / 2,
 * rather than the conventional near * tan(fov / 2). Three.js needs the
 * geometrically equivalent conventional vertical FOV.
 */
export function effectiveLegacyVerticalFov(declaredDegrees: number): number {
  const declaredRadians = THREE.MathUtils.degToRad(declaredDegrees);
  return THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(declaredRadians) / 2));
}
